import { memo, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { FlexBox } from '@dfl/mui-react-common';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@dfl/react-security';
import { AuthLayout } from 'layouts';
import { LogoView } from 'components/Logo/LogoView';
import themeConfig from 'settings/branding';
import AuthBanner from 'modules/authentication/components/AuthBanner';
import { logoutCurrentSession } from 'modules/notification/services/auth-session.service';
import MfaMethodSelector from 'modules/authentication/components/MfaMethodSelector/MfaMethodSelector';
import MfaShieldHero from 'modules/authentication/components/MfaShieldHero/MfaShieldHero';
import { MfaBackupCodesDialog, MfaSetupDialog } from 'modules/authentication/components/MfaSetup';
import { BackupCodesResponse } from 'modules/authentication/services/mfa.types';
import MfaEnrollmentSuccess from './MfaEnrollmentSuccess';
import { MfaSetupMethod } from '../MfaMethodSelector/MfaMethodSelector';

type MfaEnrollmentRequiredProps = {
  email?: string;
  onCompleted: () => void;
};

const MfaEnrollmentRequired = ({ email, onCompleted }: MfaEnrollmentRequiredProps) => {
  const { t } = useTranslation('authentication');
  const { logout } = useAuth();
  const [method, setMethod] = useState<MfaSetupMethod>('TOTP');
  const [setupMethod, setSetupMethod] = useState<MfaSetupMethod>();
  const [backupCodes, setBackupCodes] = useState<string[]>();
  const [enrolled, setEnrolled] = useState(false);

  const handleSetupSuccess = (response: BackupCodesResponse) => {
    setSetupMethod(undefined);
    const codes = response?.backupCodes;
    // A verified setup must never dead-end: if the backend returns no codes, move on anyway.
    if (codes?.length) setBackupCodes(codes);
    else setEnrolled(true);
  };

  const handleBackupCodesClosed = () => {
    setBackupCodes(undefined);
    setEnrolled(true);
  };

  const handleLogout = async () => {
    try {
      await logoutCurrentSession();
    } catch (error) {
      console.error('Unable to revoke the backend session', error);
    } finally {
      await logout();
    }
  };

  return (
    <AuthLayout>
      <FlexBox sx={{ minHeight: 550, flexDirection: { xs: 'column', md: 'row' } }}>
        <div className='p-8 md:p-12 w-full flex flex-col justify-center'>
          <div className='mb-5 flex items-center justify-center'>
            <LogoView src={themeConfig?.logoUrl} width={186} height={53} />
          </div>

          {enrolled ? (
            <MfaEnrollmentSuccess onDone={onCompleted} />
          ) : (
            <>
              <MfaShieldHero variant='inline' size={64} />

              <Typography variant='h1' fontWeight={400} textAlign='center' mt={2}>
                {t('mfa.enrollment.title')}
              </Typography>
              <Typography color='text.secondary' textAlign='center' mt={1} mb={4}>
                {t('mfa.enrollment.subtitle')}
              </Typography>

              <Typography fontWeight={600} mb={0.5}>
                {t('mfa.methodChooser.title')}
              </Typography>
              <Typography variant='body2' color='text.secondary' mb={2}>
                {t('mfa.methodChooser.subtitle')}
              </Typography>

              <MfaMethodSelector value={method} onChange={setMethod} email={email} />

              <Stack spacing={1} alignItems='center' mt={4}>
                <Button fullWidth variant='contained' size='large' onClick={() => { setSetupMethod(method); }}>
                  {t('mfa.enrollment.continue')}
                </Button>
                <Button variant='text' color='inherit' size='small' onClick={handleLogout}>
                  {t('mfa.enrollment.logout')}
                </Button>
              </Stack>
            </>
          )}
        </div>
        <AuthBanner />
      </FlexBox>

      {setupMethod && (
        <MfaSetupDialog
          method={setupMethod}
          onSuccess={handleSetupSuccess}
          onClose={() => { setSetupMethod(undefined); }}
        />
      )}
      {backupCodes && <MfaBackupCodesDialog backupCodes={backupCodes} onClose={handleBackupCodesClosed} />}
    </AuthLayout>
  );
};

export default memo(MfaEnrollmentRequired);
